import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  BookmarkCreate,
  ButtonGroupNav,
  ClearButton,
  InfoIcon,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { Button, Flex } from 'antd';
import * as Icon from 'react-feather';
import styled from 'styled-components';
import { useSectionContext } from '../../common/SectionContext';
import translations from './OutputDataHeader.translations.json';
import { Helmet } from 'react-helmet-async';
import { OutputDataTimeSeriesButton } from './errorReport/OutputDataTimeSeriesButton';
import { OutputDataTimeSeriesActionKey } from './OutputDataDirectory';
import React, { useMemo } from 'react';
import { getPeriodToDays } from '../input-data/utils';

const { addTranslations, useTranslation } = i18nNsFactory('output-data-header');
addTranslations(translations);

export interface OutputDataHeaderProps {
  valueDate?: [string, string];
  filtersCount: number;
  onFilterClear: () => void;
  onFilterToggle?: () => void;
  isFilterOpen?: boolean;
  onRefresh?: () => void;
  isLoading?: boolean;
  onTimeSeriesAction?: (actionKey: OutputDataTimeSeriesActionKey) => void;
  infoIconUrl?: InfoIconUrl;
}

export function OutputDataHeader({
  valueDate,
  filtersCount,
  onFilterClear,
  onFilterToggle,
  isFilterOpen,
  onRefresh,
  isLoading = false,
  onTimeSeriesAction,
  infoIconUrl,
}: OutputDataHeaderProps) {
  const { t } = useTranslation();
  const { basePath } = useSectionContext();

  const valueDays = useMemo(() => getPeriodToDays(valueDate), [valueDate]);

  return (
    <>
      <Helmet>
        <title>{t('header')}</title>
      </Helmet>
      <Flex justify="space-between">
        <Flex gap="middle">
          <Title>{t('header')}</Title>
          {infoIconUrl && <InfoIcon infoIconUrl={infoIconUrl}></InfoIcon>}
          <BookmarkCreate
            excludeUrlParams={['valueDate']}
            includeUrlParams={
              !valueDays
                ? []
                : [{ key: 'valueDays', value: valueDays.toString() }]
            }
          />
          <ButtonGroupNav
            items={[
              {
                key: 'processingState',
                label: t('view.processingState'),
                to: basePath,
                disabled: isLoading,
              },
              {
                key: 'errors',
                label: t('view.errors'),
                to: `${basePath}/errors`,
                disabled: isLoading,
              },
            ]}
          />
        </Flex>
        <Flex gap="small" justify="end">
          {onTimeSeriesAction && (
            <OutputDataTimeSeriesButton
              disabled={isLoading}
              onAction={onTimeSeriesAction}
            />
          )}
          {!!onRefresh && (
            <Button
              id="refresh-directory"
              onClick={() => onRefresh()}
              loading={isLoading}
            >
              <Flex gap="small" align="center">
                {t('button.refresh')}
              </Flex>
            </Button>
          )}
          {filtersCount > 0 && (
            <Flex justify="end">
              <ClearButton
                id="clear-output-data-dir-filters"
                count={filtersCount}
                onClick={onFilterClear}
                disabled={isLoading}
              />
            </Flex>
          )}
          <Button
            id="toggle-directory-filter"
            type="primary"
            onClick={onFilterToggle}
            disabled={isLoading}
          >
            <Flex gap="small" align="center">
              <Icon.Filter size={14} />
              {isFilterOpen
                ? t('button.close-filter')
                : t('button.open-filter')}
            </Flex>
          </Button>
        </Flex>
      </Flex>
    </>
  );
}

const Title = styled.h3`
  align-self: flex-start;
`;
